/**
 * Shared i18n configuration for every ReHub app.
 *
 * This is the single source of truth for which languages the platform
 * supports and the cookie used to remember a visitor's choice across
 * subdomains. Add a new language here and ship its translation file in
 * `translations/<code>.json`.
 */

export type LanguageCode = 'en' | 'es' | 'fr' | 'de' | 'pt';

export interface LanguageOption {
  code: LanguageCode;
  /** Native label shown in the switcher. */
  label: string;
  /** Emoji flag for the switcher. */
  flag: string;
}

export const SUPPORTED_LANGUAGES: readonly LanguageOption[] = [
  { code: 'en', label: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'es', label: 'Espa\u00f1ol', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'fr', label: 'Fran\u00e7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'de', label: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'pt', label: 'Portugu\u00eas', flag: '\u{1F1F5}\u{1F1F9}' },
] as const;

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/** Cookie/localStorage key used to persist the visitor's language choice. */
export const LANG_STORAGE_KEY = 'rehub_lang';

export const SUPPORTED_LANGUAGE_CODES: readonly LanguageCode[] =
  SUPPORTED_LANGUAGES.map((l) => l.code);

/** Type guard: is the given string one of our supported language codes? */
export function isSupportedLanguage(value: unknown): value is LanguageCode {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LANGUAGE_CODES as readonly string[]).includes(value)
  );
}
